import React from 'react';
import {
  Box,
  Heading,
  Flex,
  Text,
  Badge,
  Button,
  Spinner,
  Avatar,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Checkbox,
  Input,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload } from 'react-icons/fi';
import dayjs from 'dayjs';
import xhr from 'xhr';
import RegisterPaymentModal from './components/RegisterPaymentModal';

const STATUS_MAP = {
  pending: { label: 'Pendiente', color: 'yellow' },
  paid: { label: 'Pagado', color: 'green' },
};

const METHOD_LABELS = {
  transferencia: 'Transferencia',
  consignacion: 'Consignación',
  otro: 'Otro',
};

function exportToCSV(rows, filename) {
  const header = ['Materia', 'Programa', 'Estudiante', 'Fecha', 'Monto', 'Estado'];
  const lines = rows.map((c) => {
    const attrs = c.attributes || c;
    const studentUser = attrs.student?.data?.attributes?.user?.data?.attributes || attrs.student?.user;
    const studentName = studentUser ? `${studentUser.first_name || ''} ${studentUser.last_name || ''}`.trim() : '';
    return [
      attrs.concept,
      attrs.program_name || '',
      studentName,
      dayjs(attrs.charge_date).format('YYYY-MM-DD'),
      attrs.amount,
      STATUS_MAP[attrs.status]?.label || attrs.status,
    ]
      .map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`)
      .join(',');
  });

  const csv = [header.join(','), ...lines].join('\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function CobrosDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [school, setSchool] = React.useState(null);
  const [charges, setCharges] = React.useState([]);
  const [payments, setPayments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [period, setPeriod] = React.useState('');
  const [selected, setSelected] = React.useState([]);

  const fetchData = React.useCallback(async () => {
    try {
      const [schoolRes, chargesRes, paymentsRes] = await Promise.all([
        xhr(`/schools/${id}`),
        xhr(
          `/school-usage-charges?filters[school][id][$eq]=${id}&populate=student.user&sort[0]=charge_date:desc&pagination[pageSize]=500`
        ),
        xhr(
          `/school-usage-payments?filters[school][id][$eq]=${id}&populate=registered_by,charges&sort[0]=payment_date:desc&pagination[pageSize]=200`
        ),
      ]);

      setSchool(schoolRes.data.data);
      setCharges(chargesRes.data.data || []);
      setPayments(paymentsRes.data.data || []);
    } catch (error) {
      console.error('Error fetching cobros detalle:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  const schoolAttrs = school?.attributes || school || {};

  const filteredCharges = period
    ? charges.filter((c) => dayjs((c.attributes || c).charge_date).format('YYYY-MM') === period)
    : charges;

  const selectedCharges = filteredCharges.filter(
    (c) => selected.includes(c.id) && (c.attributes || c).status === 'pending' && (c.attributes || c).has_tariff
  );
  const selectedTotal = selectedCharges.reduce((acc, c) => acc + Number((c.attributes || c).amount || 0), 0);

  const toggleSelect = (charge) => {
    const attrs = charge.attributes || charge;
    if (attrs.status !== 'pending' || !attrs.has_tariff) return;

    setSelected((prev) => (prev.includes(charge.id) ? prev.filter((x) => x !== charge.id) : [...prev, charge.id]));
  };

  return (
    <Box p={4}>
      <Flex alignItems="center" gap={3} mb={4}>
        <Button leftIcon={<FiArrowLeft />} variant="ghost" onClick={() => navigate('/plataforma/administrador/instituciones/cobros')}>
          Volver
        </Button>
      </Flex>

      <Flex alignItems="center" gap={3} mb={6}>
        <Avatar size="md" name={schoolAttrs.name} src={schoolAttrs.logo_url} />
        <Box>
          <Heading size="md">{schoolAttrs.name}</Heading>
          <Text fontSize="sm" color="gray.500">
            {schoolAttrs.domain}
          </Text>
        </Box>
      </Flex>

      <Tabs colorScheme="purple">
        <TabList>
          <Tab>Cargos</Tab>
          <Tab>Pagos</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <Flex justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={3}>
              <Flex alignItems="center" gap={3}>
                <Text fontSize="sm" color="gray.600">
                  Periodo
                </Text>
                <Input
                  type="month"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  maxW="200px"
                  bg="white"
                />
                {period && (
                  <Button size="sm" variant="ghost" onClick={() => setPeriod('')}>
                    Limpiar
                  </Button>
                )}
              </Flex>
              <Button
                leftIcon={<FiDownload />}
                variant="outline"
                colorScheme="purple"
                onClick={() => exportToCSV(filteredCharges, `cargos-${schoolAttrs.domain || id}.csv`)}
              >
                Exportar CSV
              </Button>
            </Flex>

            <TableContainer bg="white" borderRadius="lg" boxShadow="sm">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th></Th>
                    <Th>Materia</Th>
                    <Th>Programa</Th>
                    <Th>Estudiante</Th>
                    <Th>Fecha</Th>
                    <Th isNumeric>Monto</Th>
                    <Th>Estado</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredCharges.map((c) => {
                    const attrs = c.attributes || c;
                    const studentUser =
                      attrs.student?.data?.attributes?.user?.data?.attributes || attrs.student?.user;
                    const studentName = studentUser
                      ? `${studentUser.first_name || ''} ${studentUser.last_name || ''}`.trim()
                      : '—';
                    const statusInfo = STATUS_MAP[attrs.status] || STATUS_MAP.pending;
                    const selectable = attrs.status === 'pending' && attrs.has_tariff;

                    return (
                      <Tr key={c.id} opacity={attrs.has_tariff ? 1 : 0.6}>
                        <Td>
                          <Tooltip
                            label={!attrs.has_tariff ? 'Sin tarifa configurada para este programa' : ''}
                            isDisabled={attrs.has_tariff}
                          >
                            <Checkbox
                              isChecked={selected.includes(c.id)}
                              isDisabled={!selectable}
                              onChange={() => toggleSelect(c)}
                            />
                          </Tooltip>
                        </Td>
                        <Td maxW="260px">
                          <Flex alignItems="center" gap={2}>
                            <Tooltip label={attrs.concept} openDelay={300}>
                              <Text noOfLines={1}>{attrs.concept}</Text>
                            </Tooltip>
                            {!attrs.has_tariff && (
                              <Badge colorScheme="orange" flexShrink={0}>
                                Sin tarifa
                              </Badge>
                            )}
                          </Flex>
                        </Td>
                        <Td maxW="240px">
                          <Tooltip label={attrs.program_name || '—'} openDelay={300}>
                            <Text noOfLines={1} color={attrs.program_name ? 'inherit' : 'gray.400'}>
                              {attrs.program_name || '—'}
                            </Text>
                          </Tooltip>
                        </Td>
                        <Td maxW="180px">
                          <Tooltip label={studentName} openDelay={300}>
                            <Text noOfLines={1}>{studentName}</Text>
                          </Tooltip>
                        </Td>
                        <Td>{dayjs(attrs.charge_date).format('DD/MM/YYYY')}</Td>
                        <Td isNumeric>${Number(attrs.amount || 0).toLocaleString()}</Td>
                        <Td>
                          <Badge colorScheme={statusInfo.color}>{statusInfo.label}</Badge>
                        </Td>
                      </Tr>
                    );
                  })}
                  {filteredCharges.length === 0 && (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={8} color="gray.500">
                        No hay cargos registrados
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>

            <Flex justifyContent="flex-end" alignItems="center" gap={4} mt={4}>
              <Text fontWeight="bold">
                Total seleccionado: ${selectedTotal.toLocaleString()} COP ({selectedCharges.length} cargo
                {selectedCharges.length === 1 ? '' : 's'})
              </Text>
              <Button colorScheme="purple" isDisabled={selectedCharges.length === 0} onClick={onOpen}>
                Registrar pago
              </Button>
            </Flex>
          </TabPanel>

          <TabPanel px={0}>
            <TableContainer bg="white" borderRadius="lg" boxShadow="sm">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Fecha</Th>
                    <Th>Referencia</Th>
                    <Th>Método</Th>
                    <Th>Registrado por</Th>
                    <Th>Conceptos</Th>
                    <Th isNumeric>Monto</Th>
                    <Th isNumeric># Cargos</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {payments.map((p) => {
                    const attrs = p.attributes || p;
                    const registeredBy = attrs.registered_by?.data?.attributes || attrs.registered_by;
                    const registeredByName = registeredBy
                      ? `${registeredBy.first_name || ''} ${registeredBy.last_name || ''}`.trim()
                      : '—';
                    const chargesList = attrs.charges?.data || attrs.charges || [];
                    const chargeItems = chargesList.map((ch) => {
                      const chAttrs = ch.attributes || ch;
                      return {
                        concept: chAttrs.concept || '',
                        program: chAttrs.program_name || '',
                      };
                    });
                    const summaryText = chargeItems
                      .map((it) => (it.program ? `${it.concept} (${it.program})` : it.concept))
                      .filter(Boolean)
                      .join(', ');
                    const tooltipNode = chargeItems.length ? (
                      <Box>
                        {chargeItems.map((it, idx) => (
                          <Text key={idx} fontSize="xs">
                            • {it.concept}
                            {it.program ? ` — ${it.program}` : ''}
                          </Text>
                        ))}
                      </Box>
                    ) : (
                      ''
                    );

                    return (
                      <Tr key={p.id}>
                        <Td>{dayjs(attrs.payment_date).format('DD/MM/YYYY')}</Td>
                        <Td>{attrs.bank_reference}</Td>
                        <Td>{METHOD_LABELS[attrs.method] || attrs.method}</Td>
                        <Td>{registeredByName}</Td>
                        <Td maxW="320px">
                          <Tooltip label={tooltipNode} openDelay={300} isDisabled={!chargeItems.length}>
                            <Text noOfLines={1} color={summaryText ? 'inherit' : 'gray.400'}>
                              {summaryText || '—'}
                            </Text>
                          </Tooltip>
                        </Td>
                        <Td isNumeric>${Number(attrs.amount || 0).toLocaleString()}</Td>
                        <Td isNumeric>{chargesList.length}</Td>
                      </Tr>
                    );
                  })}
                  {payments.length === 0 && (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={8} color="gray.500">
                        No hay pagos registrados
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <RegisterPaymentModal
        isOpen={isOpen}
        onClose={onClose}
        school={school}
        selectedCharges={selectedCharges}
        total={selectedTotal}
        onSuccess={() => {
          setSelected([]);
          fetchData();
        }}
      />
    </Box>
  );
}
